export type InspType = 'BC'|'VC'|'PT'|'RT'|'MT'|'UT'|'PMI'|'PWHT';

export interface InspStatusParams {
  inspType: InspType;
  section?: string[];
  serviceclass_name?: string[];
  line_no?: string[];
  pipesize_name?: string[];
  weldtype_name?: string[];
  fluid?: string[];
}

const pickOne = (arr?: string[]) => (Array.isArray(arr) && arr.length ? String(arr[0]) : undefined);
const sq = (s?: string) => (s == null ? 'NULL' : `'${s.replace(/'/g, "''")}'`);

export const buildInspStatusQuery = (p: InspStatusParams) => {
  const insp = sq(p.inspType);
  const section = sq(pickOne(p.section));
  const svc = sq(pickOne(p.serviceclass_name));
  const line = sq(pickOne(p.line_no));
  const size = sq(pickOne(p.pipesize_name));
  const wtype = sq(pickOne(p.weldtype_name));
  const fluid = sq(pickOne(p.fluid));

  // Backend only allows queries starting with SELECT. Wrap the CTE in a subquery and order outside.
  return `
SELECT * FROM (
  WITH base AS (
    SELECT DISTINCT ON (l.weldingdata_id, l.weld_no_rev)
           l.project_id, l.weldingdata_id, l.weld_no_rev, l.line_no, l.section,
           l.serviceclass_name, l.fluid, l.weldtype_name, l.pipesize_name, l.spool_id
    FROM public.qaqc_pipe_weldingdata_log l
    WHERE (${section} IS NULL OR l.section = ${section})
      AND (${svc} IS NULL OR l.serviceclass_name = ${svc})
      AND (${line} IS NULL OR l.line_no ILIKE '%'||${line}||'%')
      AND (${size} IS NULL OR l.pipesize_name = ${size})
      AND (${wtype} IS NULL OR l.weldtype_name = ${wtype})
      AND (${fluid} IS NULL OR l.fluid = ${fluid})
    ORDER BY l.weldingdata_id, l.weld_no_rev, l.mod_date DESC
  ), joined AS (
    SELECT 
      b.*,
      -- pick only needed result fields to avoid name clashes
      r.bc_date, r.bc_report_no, r.bc_inspector,
      r.vc_date, r.vc_report_no, r.vc_inspector,
      r.fw_date, r.flw_date,
      r.pt_date, r.pt_report_no, r.pt_result,
      r.rt_date, r.rt_report_no, r.rt_result,
      r.mt_date, r.mt_report_no, r.mt_result,
      r.ut_date, r.ut_report_no, r.ut_result,
      r.pmi_date, r.pmi_report_no,
      r.pwht_date, r.pwht_report_no, r.pwht_inspector, r.pwht_hardness,
      rqd.pt_required_rate, rqd.rt_required_rate, rqd.mt_required_rate, rqd.ut_required_rate,
      rqd.bc_rate, rqd.vc_rate, rqd.pmi_rate, rqd.flw_rate, rqd.fw_rate, rqd.pwht_required_hardness
    FROM base b
    JOIN (
      SELECT DISTINCT ON (weldingdata_id, weld_no_rev)
             weldingdata_id, weld_no_rev,
             bc_date, bc_report_no, bc_inspector,
             vc_date, vc_report_no, vc_inspector,
             fw_date, flw_date,
             pt_date, pt_report_no, pt_result,
             rt_date, rt_report_no, rt_result,
             mt_date, mt_report_no, mt_result,
             ut_date, ut_report_no, ut_result,
             pmi_date, pmi_report_no,
             pwht_date, pwht_report_no, pwht_inspector, pwht_hardness,
             updated_at, created_at, id
      FROM public.qaqc_pipe_weldingdata_result
      ORDER BY weldingdata_id, weld_no_rev,
        GREATEST(
          COALESCE(bc_date,  DATE '0001-01-01'),
          COALESCE(vc_date,  DATE '0001-01-01'),
          COALESCE(fw_date,  DATE '0001-01-01'),
          COALESCE(flw_date, DATE '0001-01-01'),
          COALESCE(pt_date,  DATE '0001-01-01'),
          COALESCE(rt_date,  DATE '0001-01-01'),
          COALESCE(mt_date,  DATE '0001-01-01'),
          COALESCE(ut_date,  DATE '0001-01-01'),
          COALESCE(pmi_date, DATE '0001-01-01'),
          COALESCE(pwht_date,DATE '0001-01-01')
        ) DESC
    ) r
      ON r.weldingdata_id = b.weldingdata_id AND r.weld_no_rev = b.weld_no_rev
    LEFT JOIN (
      SELECT project_id, serviceclass_name, fluid, weldtype_name,
             MAX(pt_required_rate) AS pt_required_rate,
             MAX(rt_required_rate) AS rt_required_rate,
             MAX(mt_required_rate) AS mt_required_rate,
             MAX(ut_required_rate) AS ut_required_rate,
             MAX(bc_rate) AS bc_rate,
             MAX(vc_rate) AS vc_rate,
             MAX(pmi_rate) AS pmi_rate,
             MAX(flw_rate) AS flw_rate,
             MAX(fw_rate) AS fw_rate,
             MAX(pwht_required_hardness) AS pwht_required_hardness
      FROM public.vw_qaqc_pipe_inspection_rqd
      GROUP BY project_id, serviceclass_name, fluid, weldtype_name
    ) rqd
      ON rqd.project_id = b.project_id
     AND rqd.serviceclass_name = b.serviceclass_name
     AND rqd.fluid = b.fluid
     AND rqd.weldtype_name = b.weldtype_name
  ), flags AS (
    SELECT *,
      -- prerequisite completion flags
      (r.fw_date IS NOT NULL) AS fw_done,
      (r.flw_date IS NOT NULL) AS flw_done,
      (r.vc_date IS NOT NULL OR r.vc_report_no IS NOT NULL OR r.vc_inspector IS NOT NULL) AS vc_done,
      -- eligibility by inspection dependencies
      CASE UPPER(${insp})
        WHEN 'VC' THEN ((r.fw_date IS NOT NULL) AND (r.flw_date IS NOT NULL))
        WHEN 'PT' THEN (r.vc_date IS NOT NULL OR r.vc_report_no IS NOT NULL OR r.vc_inspector IS NOT NULL)
        WHEN 'RT' THEN (r.vc_date IS NOT NULL OR r.vc_report_no IS NOT NULL OR r.vc_inspector IS NOT NULL)
        WHEN 'MT' THEN (r.vc_date IS NOT NULL OR r.vc_report_no IS NOT NULL OR r.vc_inspector IS NOT NULL)
        WHEN 'UT' THEN (r.vc_date IS NOT NULL OR r.vc_report_no IS NOT NULL OR r.vc_inspector IS NOT NULL)
        ELSE TRUE
      END AS eligible,
      CASE UPPER(${insp})
        WHEN 'BC'  THEN (bc_rate IS NOT NULL)
        WHEN 'VC'  THEN (vc_rate IS NOT NULL)
        WHEN 'PT'  THEN (pt_required_rate IS NOT NULL)
        WHEN 'RT'  THEN (rt_required_rate IS NOT NULL)
        WHEN 'MT'  THEN (mt_required_rate IS NOT NULL)
        WHEN 'UT'  THEN (ut_required_rate IS NOT NULL)
        WHEN 'PMI' THEN (pmi_rate IS NOT NULL)
        WHEN 'PWHT' THEN (pwht_required_hardness IS NOT NULL)
        ELSE FALSE
      END AS required,
      CASE UPPER(${insp})
        WHEN 'BC'  THEN (r.bc_date IS NOT NULL OR r.bc_report_no IS NOT NULL OR r.bc_inspector IS NOT NULL)
        WHEN 'VC'  THEN (r.vc_date IS NOT NULL OR r.vc_report_no IS NOT NULL OR r.vc_inspector IS NOT NULL)
        WHEN 'PT'  THEN (r.pt_date IS NOT NULL OR r.pt_report_no IS NOT NULL OR r.pt_result IS NOT NULL)
        WHEN 'RT'  THEN (r.rt_date IS NOT NULL OR r.rt_report_no IS NOT NULL OR r.rt_result IS NOT NULL)
        WHEN 'MT'  THEN (r.mt_date IS NOT NULL OR r.mt_report_no IS NOT NULL OR r.mt_result IS NOT NULL)
        WHEN 'UT'  THEN (r.ut_date IS NOT NULL OR r.ut_report_no IS NOT NULL OR r.ut_result IS NOT NULL)
        WHEN 'PMI' THEN (r.pmi_date IS NOT NULL OR r.pmi_report_no IS NOT NULL)
        WHEN 'PWHT' THEN (r.pwht_date IS NOT NULL OR r.pwht_report_no IS NOT NULL OR r.pwht_inspector IS NOT NULL OR r.pwht_hardness IS NOT NULL)
        ELSE FALSE
      END AS done
    FROM joined r
  )
  SELECT weldingdata_id, weld_no_rev, line_no, section, serviceclass_name, fluid, weldtype_name, pipesize_name, spool_id,
         (fw_done AND flw_done) AS wd,
         -- Only treat as required when prerequisites are satisfied or the item is already done
         (required AND (eligible OR done)) AS required,
         done,
         eligible
  FROM flags
  WHERE (fw_done AND flw_done)
) t
ORDER BY t.line_no, t.weldingdata_id, t.weld_no_rev`;
};
